'use client'

import { useTranslations } from 'next-intl'
import { useMemo } from 'react'

import {
  ConditionTreeEditor,
  useConditionKindOptions,
} from '@/features/order/order-form/components/widget/collapsible/condition-editors/condition-tree-editor'
import { ConditionEditorDataProvider } from '@/features/order/order-form/components/widget/collapsible/condition-editors/field-controls'
import {
  FormInput,
  FormSection,
} from '@/features/order/order-form/components/widget/order-form-fields'
import { useInstrumentMap } from '@/features/order/order-form/hooks/use-instrument-map'
import { useOrderFormTradeVenue } from '@/features/order/order-form/hooks/use-order-form-trade-venue'
import { useUnifiedOrderFormContext } from '@/features/order/order-form/lib/order-form-context'
import { resolveConditionTree } from '@/features/order/order-form/lib/resolve-condition-tree'
import {
  type ConditionNode,
  createNode,
} from '@/features/order/order-form/lib/trigger-condition-model'
import type { Option } from '@/features/order/order-form/lib/trigger-select-options'
import { formatSymbol } from '@/lib/utils/format-util'
import { useAccountPrimaryExchange } from '@/service/account-exchanges.hook'
import { useGetPositionGroups } from '@/service/position-group.hook'
import type { OrderFormCollapsibleConfig } from '@/state/order'

export default function EntryConditionContent({
  config,
  updateConfig,
}: {
  config: Required<OrderFormCollapsibleConfig>
  updateConfig: (patch: Partial<OrderFormCollapsibleConfig>) => void
}) {
  const t = useTranslations('orderForm')
  const { fields, orderAccounts, cardId } = useUnifiedOrderFormContext()
  const cardTradeVenue = useOrderFormTradeVenue(cardId)
  const primaryExchange = useAccountPrimaryExchange(cardTradeVenue)
  const allInstrumentsMap = useInstrumentMap(primaryExchange)
  const allInstruments = useMemo(() => Array.from(allInstrumentsMap.values()), [allInstrumentsMap])
  // Groups belong to the user (not the card's account) — the same list everywhere.
  const { data: groupData } = useGetPositionGroups()

  const defaultSymbol = fields[0]?.symbol || ''
  // The ticket's own first row account — never another widget's account picker.
  const defaultAccountId = fields[0]?.accountId || orderAccounts[0]?.accountId || ''

  const accountOptions = useMemo<Option<string>[]>(
    () => orderAccounts.map((account) => ({ value: account.accountId, label: account.name })),
    [orderAccounts]
  )

  const symbolOptions = useMemo<Option<string>[]>(() => {
    const set = new Set<string>()
    fields.forEach((f) => f.symbol && set.add(f.symbol))
    allInstruments.forEach((i) => i.symbol && set.add(i.symbol))
    return Array.from(set).map((s) => ({ value: s, label: formatSymbol(s) }))
  }, [fields, allInstruments])

  const assetOptions = useMemo<Option<string>[]>(() => {
    const set = new Set<string>()
    allInstruments.forEach((i) => {
      if (i.baseCurrency) set.add(i.baseCurrency)
    })
    return Array.from(set)
      .sort()
      .map((a) => ({ value: a, label: a }))
  }, [allInstruments])

  const groupOptions = useMemo<Option<string>[]>(
    () => (groupData?.groups ?? []).map((g) => ({ value: g.groupId, label: g.name })),
    [groupData]
  )

  // Same resolution path as the submit handler, header badge, and validation
  // (resolveConditionTree), defaulting to a price leaf so the editor always has
  // a node to render.
  const root: ConditionNode = useMemo(
    () =>
      resolveConditionTree(config) ??
      createNode('price', Date.now(), { symbol: defaultSymbol, accountId: defaultAccountId }),
    [config, defaultAccountId, defaultSymbol]
  )

  const editorData = useMemo(
    () => ({
      symbolOptions,
      assetOptions,
      groupOptions,
      accountOptions,
      defaultSymbol,
      defaultAccountId,
    }),
    [symbolOptions, assetOptions, groupOptions, accountOptions, defaultSymbol, defaultAccountId]
  )

  // Resolved once here and threaded into the recursive tree (see the hook).
  const kindOptions = useConditionKindOptions()

  const setRoot = (next: ConditionNode) =>
    updateConfig({ entryConditionTreeJson: JSON.stringify(next) })

  // A schedule trigger already fires at a fixed time, so a separate "must fire
  // before T" expiry is meaningless there — hide it for a single schedule root.
  const scheduleOnly = root.kind === 'schedule'
  const expiryMinutes = config.entryConditionExpiryMinutes || ''

  return (
    <ConditionEditorDataProvider value={editorData}>
      <div className="flex flex-col gap-2">
        <ConditionTreeEditor node={root} onChange={setRoot} kindOptions={kindOptions} />

        {!scheduleOnly && (
          <FormSection label={t('expiry')}>
            <FormInput
              aria-label={t('expiry')}
              inputMode="numeric"
              unit="min"
              value={expiryMinutes}
              onChange={(e) => {
                const v = e.target.value
                if (v === '' || /^\d*$/.test(v)) updateConfig({ entryConditionExpiryMinutes: v })
              }}
              placeholder="60"
            />
          </FormSection>
        )}
      </div>
    </ConditionEditorDataProvider>
  )
}
